import { getRulesStore, getScreenTimeStore } from '../storage/stores';
import { isDelegationStarted } from '../delegation/delegation';
import { isSafeNetworkDelegated } from '../safeNetworks/safeNetworksDelegation';
import {
  getDateInFuture,
  getDateMs,
  getSystemTimezone,
  getWeekdayValue,
  isWeekend,
  now,
  toSeconds,
} from '../lib/dateTime';
import { DeviceAndUserStore } from '../types/store';
import { UNLIMITED_DAILY_QUOTA } from '../constants';
import { shouldCheckNavigation } from '../helper/rules';
import { TimeRestrictions } from '../types/api';
import { ValueOf } from '../types';

/**
 * Correct different inconsistencies in the daily quotas received from the API.
 *
 * API has a weird format. If is_weekend_quota is `true`, then the fields `sat`
 * and `sun` have a value of 120 by default. If is_weekdays_quota is `true`,
 * then the fields `mon`, `tue`, `wed`, `thr`, `fri` have a value of 120 by
 * default.
 * The flags are redundant from the perspective of the app, so we simply use
 * these flags to set the corresponding days to zero hours restriction.
 */
export const parseDailyQuotas = (
  timeRestrictions: TimeRestrictions | undefined
) => {
  if (timeRestrictions === undefined) {
    return undefined;
  }
  const {
    is_weekdays_quota: isWeekdaysQuota = false,
    is_weekend_quota: isWeekendQuota = false,
  } = timeRestrictions;

  return Object.entries(timeRestrictions.quotas).reduce((acc, entry) => {
    const [day, quota] = entry as [
      keyof TimeRestrictions['quotas'],
      ValueOf<TimeRestrictions['quotas']>
    ];

    if (
      (isWeekend(day) && !isWeekendQuota) ||
      (!isWeekend(day) && !isWeekdaysQuota)
    ) {
      return { ...acc, [day]: UNLIMITED_DAILY_QUOTA };
    }

    return { ...acc, [day]: quota };
  }, {} as TimeRestrictions['quotas']);
};

const getConsumedQuotaSeconds = (deviceUserData: DeviceAndUserStore) =>
  isDelegationStarted() && !isSafeNetworkDelegated()
    ? toSeconds(
        getScreenTimeStore().getScreenTime()?.totalConsumedInMillis ?? 0
      )
    : deviceUserData.consumedQuotaSeconds;

export const isOverDailyQuota = (
  deviceUserData: DeviceAndUserStore,
  date: number,
  consumedQuotaSeconds: number = getConsumedQuotaSeconds(deviceUserData),
  { shouldLog = false }: { shouldLog?: boolean } = {}
) => {
  const rulesStore = getRulesStore();
  const timeRestrictions = rulesStore?.getTimeRestrictions();
  const timezone = rulesStore?.getTimezone();
  const dailyQuotas = parseDailyQuotas(timeRestrictions);

  if (
    timeRestrictions === undefined ||
    timezone === undefined ||
    dailyQuotas === undefined ||
    !shouldCheckNavigation(timeRestrictions)
  ) {
    if (shouldLog) {
      console.log(
        `[isOverDailyQuota] false - No restrictions or navigation check disabled: timeRestrictionsUndefined=${
          timeRestrictions === undefined
        }, timezoneUndefined=${timezone === undefined}, dailyQuotasUndefined=${
          dailyQuotas === undefined
        }, shouldCheckNavigation=${
          timeRestrictions ? shouldCheckNavigation(timeRestrictions) : false
        }, result=false`
      );
    }
    return false;
  }

  const dailyQuota = getWeekdayValue(dailyQuotas, date, timezone);

  if (shouldLog) {
    console.log(
      `[isOverDailyQuota] Daily quota calculation: date=${date}, rulesTimezone=${timezone}, systemTimezone=${getSystemTimezone()}, dailyQuota=${dailyQuota}`
    );
  }

  if (dailyQuota === UNLIMITED_DAILY_QUOTA) {
    if (shouldLog) {
      console.log('[isOverDailyQuota] false - Daily quota is unlimited');
    }
    return false;
  }

  const consumedQuotaMinutes = consumedQuotaSeconds / 60;
  const isOverQuota = consumedQuotaMinutes >= dailyQuota;

  if (shouldLog) {
    console.log(
      `[isOverDailyQuota] ${isOverQuota} - consumedQuotaSeconds=${consumedQuotaSeconds}, consumedQuotaMinutes=${consumedQuotaMinutes}, dailyQuota=${dailyQuota}`
    );
  }

  return isOverQuota;
};

export const isDailyQuotaLeftLessOrEqualThan = async (
  deviceUserData: DeviceAndUserStore,
  seconds = 0
) => {
  const timezone = getRulesStore().getTimezone();
  const futureDate = getDateInFuture({ seconds }, await now(), timezone);

  return isOverDailyQuota(
    deviceUserData,
    getDateMs(futureDate),
    getConsumedQuotaSeconds(deviceUserData) + seconds
  );
};
